<template>
    <div class="f fh fa-c dw-10 pr sp-r-l ath-sort"
        :class="[
            is_active ? 'el--active' : '',
            disabled ? 'el--disabled' : '',
        ]"
        @click="select">
        <div class="f fa-c fh dw-10" :class="uiClass" v-if="!uiSubtext">
            <slot></slot>
            <TruncatedTitle v-if="uiTitle" :uiTitle="uiPrefix + ' ' + $options.filters.athT(uiTitle)" />
        </div>
        <div class="f fa-l fv dw-10" v-else>
            <div class="f fh fa-c dw-10" :class="uiClass">
                <slot></slot>
                <strong v-if="uiTitle">
                    {{ uiPrefix }}
                    {{ uiTitle | athT }}
                </strong>
            </div>
            <span class="te-subtext dw-10 o-ol sm-t-xxs">
                {{ uiSubtext | athT }}
            </span>
        </div>
        <slot name="before-dir"></slot>
        <i class="ath-sort-dir" :class="[direction ? 'lnr-arrow-up' : 'lnr-arrow-down']"></i>
    </div>
</template>

<script>
import { guuid, isFunction, sort } from '@aretheyhappy/utils';
import TruncatedTitle from './visuals/TruncatedTitle.vue';

function extractNumber (value) {
    const result = /(-)?\$?([0-9]+)%?/.exec(value);
    if (result === null || result[2] === undefined) return 0;
    const number = parseInt(result[2]);
    return result[1] === '-' ? number * -1 : number;
}

/**
 * Sort numeric values that have unit decorators (-5%, $5, etc)
 * @param lst
 * @param key
 * @param type
 */
function internalUnitSort(lst, key, type) {
    const list = lst.sort((a, b) => {
        const aNumber = extractNumber(a[key]);
        const bNumber = extractNumber(b[key]);
        if (aNumber < bNumber) return -1;
        if (aNumber > bNumber) return 1;
        return 0;
    });

    if (type === 'desc') list.reverse();
    return list;
}

export default {
    name: 'mead-sort',
    props: {
        /**
         * Key of the field/property this control sorts by, used both as the
         * lookup key when sorting a list and as the identifier compared against
         * other Sort instances sharing the same `pubkey`.
         */
        sort        : {type: String, default: () => ''},
        /**
         * How to compare values when sorting: 'numeric' sorts raw numeric/string
         * values, 'alphabetic' sorts case-insensitively ignoring whitespace, and
         * 'numeric_unit' extracts a leading numeric value from strings with unit
         * decorators (e.g. "-5%", "$5") before comparing.
         */
        type        : {type: String, default: () => 'numeric'},
        /**
         * Disables the control, preventing selection and applying the disabled
         * styling.
         */
        disabled    : {type: Boolean, default: () => false},
        /**
         * Namespace/channel key used to scope this control's Flash pub/sub events
         * (e.g. `${pubkey}:sort_select`), coordinating with sibling Sort controls
         * and the list they operate on.
         */
        pubkey      : {type: [Boolean, String], default: () => false},
        /**
         * Text shown immediately before the translated `uiTitle`.
         */
        uiPrefix    : {type: String, default: () => ''},
        /**
         * Translation key for the control's title text, run through the `athT` filter.
         */
        uiTitle     : {type: [Boolean, String], default: () => false},
        /**
         * Initial active/direction state: `false` means inactive, any other
         * value activates the control, and `-1` specifically selects the
         * descending direction (anything else selects ascending).
         */
        uiActive    : {type: [Boolean, Number], default: () => false},
        /**
         * Translation key for optional subtext rendered beneath the title (in a
         * two-line layout) instead of the single-line layout.
         */
        uiSubtext   : {type: [Boolean, String], default: () => false},
        /**
         * Additional class name(s) applied to the inner content wrapper.
         */
        uiClass     : {type: String, default: () => ''},
    },
    components: {TruncatedTitle},
    data () {
        return {
            uid: guuid(),
            is_active: false,
            direction: false,
            truncated_tooltip: false,
        };
    },
    methods: {
        select: function () {
            if (this.disabled) return;

            const new_direction = !this.is_active ? this.direction : !this.direction;

            //  Make sure others know this one is selected
            MeadCommon.Flash.publish(`${this.pubkey}:sort_select`, {sort: this.sort, direction: new_direction}, this.uid);

            this.resort(new_direction);
        },
        resort: function (direction) {
            //  Retrieve list
            MeadCommon.Flash.publish(`${this.pubkey}:sort_retrieve`, (lst, fn) => {
                const result = this.run(lst, direction);

                //  Run sort and notify
                MeadCommon.Flash.publish(`${this.pubkey}:sort_set`, result);

                //  If function is passed run function with result
                if (isFunction(fn)) fn(result);

                this.is_active = true;
                this.direction = direction;
            });
        },
        run: function (lst, direction) {
            const key = this.sort;

            switch (`${this.type}_${direction === false ? 'asc' : 'desc'}`) {
                case 'numeric_asc':
                    return sort(lst, key, 'asc');
                case 'numeric_desc':
                    return sort(lst, key, 'desc');
                case 'alphabetic_asc':
                    return sort(lst, el => el[key].toLowerCase().replace(/\s{1,}/g, ''), 'asc');
                case 'alphabetic_desc':
                    return sort(lst, el => el[key].toLowerCase().replace(/\s{1,}/g, ''), 'desc');
                case 'numberic_unit_asc':
                    return internalUnitSort(lst, key, 'asc');
                case 'numberic_unit_desc':
                    return internalUnitSort(lst, key, 'desc');
                default:
            }
        },
        checkTruncation() {
            const el = this.$refs[this.uid];
            this.truncated_tooltip = el && el.scrollWidth > el.clientWidth;
        },
    },
    mounted() {
        this.checkTruncation();
        window.addEventListener('resize', this.checkTruncation);
    },
    created () {
        if (this.uiActive !== false) {
            this.is_active = true;
            this.direction = this.uiActive !== -1;
        }

        //  Listen to select event
        MeadCommon.Flash.subscribe(this.uid, `${this.pubkey}:sort_select`, act => {
            if (act.sort !== this.sort) {
                this.is_active = false;
                this.direction = false;
            } else {
                this.select();
            }
        });

        //  Listen to resort event
        MeadCommon.Flash.subscribe(this.uid, `${this.pubkey}:resort`, () => {
            if (!this.is_active) return;

            this.resort(this.direction);
        });

        //  Listen to run event
        MeadCommon.Flash.subscribe(this.uid, `${this.pubkey}:sort_run`, lst => {
            if (!this.is_active) return;

            MeadCommon.Flash.publish(`${this.pubkey}:sort_set`, this.run(lst, this.direction));
        });
    },
    destroyed () {
        MeadCommon.Flash.unsubscribe(this.uid);
        window.removeEventListener('resize', this.checkTruncation);
    },
};
</script>

<style lang="scss" scoped>
.ath-sort {
    cursor: pointer;

    div,
    i {
        z-index: 1
    }

    .ath-sort-dir {
        position: absolute;
        top: 50%;
        right: .5rem;
        transform: translateY(-.75rem);
        z-index: 1;
        opacity: 0;
    }

    &:hover:not(.el--active),
    &:active:not(.el--active) {
        .ath-sort-dir {
            opacity: 1;
        }
    }

    //  MODIFIERS

    &.el--active {
        box-shadow: inset 0 -2px 0 0 var(--c-txt);

        .ath-sort-dir {
            opacity: 1;
        }
    }

    &.el--disabled {
        cursor: not-allowed;
        opacity: .65;
    }
}
</style>
